import { Element, Component, Prop, State, Host, h, Watch } from '@stencil/core';
import { QRCodeVariant } from './jui-qr-code.model';
import qrcode from 'qrcode-generator';

@Component({
  tag: 'jui-qr-code',
  styleUrl: 'jui-qr-code.scss',
  shadow: true
})
export class JuiQrCode {
  @Element() elementRef: HTMLElement;

  /**
   * QR Code color: primary | secondary | success | info | warning | danger | light | dark | current
   */
  @Prop() color: QRCodeVariant = QRCodeVariant.primary;

  /**
   * QR Code Content: The QR code content. e.g. url
   */
  @Prop() contents = '';

  /**
   * QR Code Squares: Render the modules as squares
   */
  @Prop() squares = false;

  @State() data: string;
  @State() moduleCount: number;

  componentWillLoad(): void {
    this.updateQR();
  }

  render() {
    return (
      <Host>
        <div class="qr-container">
          <div class="image-container">
            <div
              class={`image-wrapper qrcode--${this.color}`}
              data-column={this.moduleCount / 2}
              data-row={this.moduleCount / 2}
            >
              <slot name="image" />
            </div>
          </div>
          <div innerHTML={this.data} />
        </div>
      </Host>
    );
  }

  @Watch('color')
  @Watch('contents')
  @Watch('squares')
  private updateQR() {
    const isUsingWebComponentPolyfill = (this.elementRef as any) === this.elementRef.shadowRoot;
    const realSlot = this.elementRef.shadowRoot.querySelector('slot');
    const hasSlot = isUsingWebComponentPolyfill
      ? !!this.elementRef.querySelector('[slot]')
      : realSlot?.assignedNodes
      ? realSlot.assignedNodes().length > 0
      : false;

    this.data = this.generateQRCode(this.contents, hasSlot);
  }

  private generateQRCode(contents: string, maskCenter: boolean) {
    const qr = qrcode(/* Auto-detect QR Code version to use */ 0, /* Highest error correction level */ 'H');
    qr.addData(contents);
    qr.make();
    const margin = 4;
    this.moduleCount = qr.getModuleCount();
    const pixelSize = this.moduleCount + margin * 2;
    const coordinateShift = pixelSize / 2;
    return `
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        height="100%"
        viewBox="${0 - coordinateShift} ${0 - coordinateShift} ${pixelSize} ${pixelSize}"
        preserveAspectRatio="xMinYMin meet">
    <rect
        width="100%"
        height="100%"
        fill="white"
        fill-opacity="0"
        cx="${-coordinateShift}"
        cy="${-coordinateShift}"/>
    ${this.renderQRDetectionPatterns(this.moduleCount, margin, coordinateShift)}
    ${this.renderQRModules(qr, this.moduleCount, margin, maskCenter, coordinateShift)}
    </svg>`;
  }

  private renderQRDetectionPatterns(count: number, margin: number, coordinateShift: number) {
    return `
      ${this.renderQRDetectionPattern(margin, margin, margin, coordinateShift)}
      ${this.renderQRDetectionPattern(count - 7 + margin, margin, margin, coordinateShift)}
      ${this.renderQRDetectionPattern(margin, count - 7 + margin, margin, coordinateShift)}
      `;
  }

  private renderQRDetectionPattern(x: number, y: number, margin: number, coordinateShift: number) {
    return `
      <path class="position-ring qrcode--${this.color}" data-column="${x - margin}" data-row="${y - margin}" d="M${
      x - coordinateShift
    } ${
      y - 0.5 - coordinateShift
    }h6s.5 0 .5 .5v6s0 .5-.5 .5h-6s-.5 0-.5-.5v-6s0-.5 .5-.5zm.75 1s-.25 0-.25 .25v4.5s0 .25 .25 .25h4.5s.25 0 .25-.25v-4.5s0-.25 -.25 -.25h-4.5z"/>
      <path class='position-center qrcode--${this.color}' data-column="${x - margin + 2}" data-row="${
      y - margin + 2
    }" d="M${x + 2 - coordinateShift} ${
      y + 1.5 - coordinateShift
    }h2s.5 0 .5 .5v2s0 .5-.5 .5h-2s-.5 0-.5-.5v-2s0-.5 .5-.5z"/>
      `;
  }

  private renderQRModules(qr: QRCode, count: number, margin: number, maskCenter: boolean, coordinateShift: number) {
    let svg = '';
    for (let column = 0; column < count; column += 1) {
      const positionX = column + margin;
      for (let row = 0; row < count; row += 1) {
        if (
          qr.isDark(column, row) &&
          !this.isPositioningElement(row, column, count) &&
          !this.isRemovableCenter(row, column, count, maskCenter)
        ) {
          const positionY = row + margin;
          svg += this.squares
            ? `
            <rect
            class='module qrcode--${this.color}'
            x="${positionX - 0.5 - coordinateShift}" y="${positionY - 0.5 - coordinateShift}" width="1" height="1" />
            `
            : `
            <circle
                class='module qrcode--${this.color}'
                cx="${positionX - coordinateShift}"
                cy="${positionY - coordinateShift}"
                data-column="${column}"
                data-row="${row}"
                r="0.5"/>`;
        }
      }
    }
    return svg;
  }

  private isPositioningElement(row: number, column: number, count: number) {
    const elemWidth = 7;
    return row <= elemWidth
      ? column <= elemWidth || column >= count - elemWidth
      : column <= elemWidth
      ? row >= count - elemWidth
      : false;
  }

  private isRemovableCenter(row: number, column: number, count: number, maskCenter: boolean) {
    if (!maskCenter) {
      return false;
    }
    const center = count / 2;
    const safelyRemovableHalf = Math.floor((count * Math.sqrt(0.1)) / 2);
    const safelyRemovableHalfX = safelyRemovableHalf;
    const safelyRemovableHalfY = safelyRemovableHalf;
    const safelyRemovableStartX = center - safelyRemovableHalfX;
    const safelyRemovableEndX = center + safelyRemovableHalfX;
    const safelyRemovableStartY = center - safelyRemovableHalfY;
    const safelyRemovableEndY = center + safelyRemovableHalfY;

    return (
      row >= safelyRemovableStartY &&
      row <= safelyRemovableEndY &&
      column >= safelyRemovableStartX &&
      column <= safelyRemovableEndX
    );
  }
}
